import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly destroyed$ = new Subject<void>();

  readonly account = signal<AccountInfo | null>(null);
  readonly roles = computed<string[]>(() => {
    const claims = this.account()?.idTokenClaims as { roles?: string[] } | undefined;
    return claims?.roles ?? [];
  });

  readonly isAdmin = computed(() => this.roles().includes('Admin'));
  readonly isCsm = computed(() => this.roles().includes('CSM'));
  readonly canSeeOperations = computed(() => this.isAdmin() || this.roles().includes('Operations'));
  readonly canSeeTechnicians = computed(() => this.isAdmin() || this.roles().includes('Technician'));

  readonly isCsmOnly = computed(() => this.isCsm() && !this.canSeeOperations() && !this.canSeeTechnicians());

  constructor() {
    this.account.set(this.msalService.instance.getActiveAccount());

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.account.set(this.msalService.instance.getActiveAccount());
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((event: EventMessage) => event.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this.destroyed$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as { account?: AccountInfo };
        if (payload?.account) {
          this.msalService.instance.setActiveAccount(payload.account);
          this.account.set(payload.account);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  logout(): void {
    this.msalService.logoutRedirect({
      account: this.msalService.instance.getActiveAccount() ?? undefined,
      postLogoutRedirectUri: `${window.location.origin}/login`,
    });
  }
}
